import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Route as RouteIcon, Pause, Play, XCircle, RotateCw, Eye } from "lucide-react";

const TONE = {
  RUNNING: "text-sky-500 bg-sky-500/10 border-sky-500/20",
  QUEUED: "text-slate-400 bg-slate-500/10 border-slate-500/20",
  WAITING: "text-amber-500 bg-amber-500/10 border-amber-500/20",
  WAITING_USER: "text-violet-500 bg-violet-500/10 border-violet-500/20",
  RETRYING: "text-orange-500 bg-orange-500/10 border-orange-500/20",
  PAUSED: "text-slate-400 bg-slate-500/10 border-slate-500/20",
  COMPLETED: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
  FAILED: "text-rose-500 bg-rose-500/10 border-rose-500/20",
  CANCELLED: "text-slate-500 bg-slate-500/10 border-slate-500/20",
};
export const RunChip = ({ status }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONE[status] || TONE.QUEUED}`}>{(status || "").replace("_", " ")}</span>
);

const OPEN = "QUEUED,RUNNING,WAITING,WAITING_USER,RETRYING,PAUSED";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", month: "short", day: "numeric" }) : "—");
const rel = (iso) => {
  if (!iso) return "—";
  const s = Math.round((new Date(iso) - Date.now()) / 1000);
  if (s <= 0) return "now";
  return s < 90 ? `in ${s}s` : s < 5400 ? `in ${Math.round(s / 60)}m` : `in ${Math.round(s / 3600)}h`;
};

/** Live table of journey runs. `embedded` hides the page header (used inside Automations). */
export default function RunsMonitor({ embedded = false }) {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [scope, setScope] = useState("open");
  const [showTest, setShowTest] = useState(false);
  const [data, setData] = useState(null);
  const botId = sp.get("bot") || "";

  const load = useCallback(() => {
    const params = { limit: 200, include_test: showTest };
    if (scope === "open") params.status = OPEN;
    else if (scope !== "all") params.status = scope;
    if (botId) params.bot_id = botId;
    return api.get("/journeys/runs", { params }).then((r) => setData(r.data)).catch((e) => { toast.error(apiErr(e)); setData({ items: [], counts: {} }); });
  }, [scope, showTest, botId]);
  useEffect(() => { load(); }, [load]);
  useLiveEvents(["journey.started", "journey.updated", "journey.completed", "journey.failed"], () => load());

  const act = async (run, action) => {
    try { await api.post(`/journeys/runs/${run.id}/${action}`); toast.success(`Run ${action}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const counts = data?.counts || {};
  const openCount = ["QUEUED", "RUNNING", "WAITING", "WAITING_USER", "RETRYING", "PAUSED"].reduce((n, k) => n + (counts[k] || 0), 0);

  return (
    <div className="space-y-4">
      {!embedded && <PageHeader title="Running Journeys" desc="Every user currently inside an automated flow — live." testId="journeys-header" />}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2"><RouteIcon size={16} className="text-primary" />
          <h3 className="font-semibold">{embedded ? "Running Journeys" : "Runs"}</h3>
          <span className="text-xs text-muted-foreground mono">{openCount} open · {counts.COMPLETED || 0} completed · {counts.FAILED || 0} failed</span></div>
        <div className="flex-1" />
        <Select value={scope} onValueChange={setScope}><SelectTrigger className="w-[170px]" data-testid="select-run-scope"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open (active)</SelectItem><SelectItem value="WAITING_USER">Waiting for user</SelectItem>
            <SelectItem value="WAITING">Delayed</SelectItem><SelectItem value="RETRYING">Retrying</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem><SelectItem value="FAILED">Failed</SelectItem><SelectItem value="all">All</SelectItem>
          </SelectContent></Select>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Switch checked={showTest} onCheckedChange={setShowTest} data-testid="switch-show-test" /> Include test runs</label>
      </div>
      {!data ? <Skeleton className="h-40 w-full" /> : data.items.length === 0 ? (
        <div className="bg-card border border-border rounded-lg"><EmptyState icon={RouteIcon} title="No journeys here"
          desc="Publish a Welcome Flow on a bot and every /start will appear here as a live run." testId="empty-journeys" /></div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/50 border-b border-border text-xs uppercase text-muted-foreground">
              {["User", "Workflow", "Current step", "Status", "Started", "Next run", "Progress", ""].map((h) => <th key={h} className="px-3 py-2.5 text-left font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.items.map((r) => {
                const done = r.progress?.done || 0, total = r.progress?.total || 0;
                const open = !["COMPLETED", "FAILED", "CANCELLED"].includes(r.status);
                return (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-secondary/40" data-testid={`run-row-${r.id}`}>
                    <td className="px-3 py-2.5"><div className="font-medium">{[r.user?.first_name, r.user?.last_name].filter(Boolean).join(" ") || r.telegram_user_id}</div>
                      <div className="text-xs text-muted-foreground mono">{r.user?.username ? "@" + r.user.username : r.telegram_user_id}{r.is_test ? " · TEST" : ""}</div></td>
                    <td className="px-3 py-2.5"><div>{r.workflow_name}</div><div className="text-xs text-muted-foreground mono">v{r.version} · {r.trigger?.type}{r.context?.start_param ? ` ${r.context.start_param}` : ""}</div></td>
                    <td className="px-3 py-2.5 mono text-xs">{r.waiting_for ? `waiting: ${r.waiting_for.kind}` : r.current_node_id ? r.current_node_id.slice(0, 8) : "—"}</td>
                    <td className="px-3 py-2.5"><RunChip status={r.status} />{r.error && <div className="text-[11px] text-rose-500 mt-1 max-w-[220px] truncate" title={r.error}>{r.error}</div>}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{fmt(r.started_at)}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.status === "WAITING" ? rel(r.resume_at) : r.status === "RETRYING" ? rel(r.next_attempt_at) : "—"}</td>
                    <td className="px-3 py-2.5 w-28"><div className="h-1.5 bg-secondary rounded overflow-hidden"><div className="h-full bg-primary" style={{ width: `${total ? Math.min(100, (done / total) * 100) : 0}%` }} /></div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 mono">{done} / {total}</div></td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="View" onClick={() => navigate(`/journeys/${r.id}`)} data-testid={`btn-view-run-${r.id}`}><Eye size={14} /></Button>
                      {open && r.status !== "PAUSED" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Pause" onClick={() => act(r, "pause")}><Pause size={14} /></Button>}
                      {r.status === "PAUSED" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Resume" onClick={() => act(r, "resume")}><Play size={14} /></Button>}
                      {r.status === "FAILED" && <Button variant="ghost" size="icon" className="h-7 w-7" title="Retry" onClick={() => act(r, "retry")}><RotateCw size={14} /></Button>}
                      {open && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" title="Cancel" onClick={() => act(r, "cancel")}><XCircle size={14} /></Button>}
                    </div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
